import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useUnbanMember } from '../../hooks/use-admin';
import axiosInstance from '../../utils/axios-config';
import {
  Ban,
  Search,
  UserCheck,
  Loader2,
  AlertTriangle,
  Calendar,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

export default function CommunityAdminBanned() {
  const { community, userRole } = useOutletContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [unbanDialog, setUnbanDialog] = useState({ open: false, member: null });

  // Fetch banned members
  const { data: bannedData, isLoading } = useQuery({
    queryKey: ['community-admin', community?.oid, 'banned'],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `/omniverse/communities/${community?.oid}/admin/members/banned`
      );
      return data;
    },
    enabled: !!community?.oid,
  });

  const unbanMutation = useUnbanMember(community?.oid);

  // Use bannedMembers from community if API doesn't have separate endpoint
  const bannedMembers =
    bannedData?.bannedMembers || community?.bannedMembers || [];

  // Filter banned members
  const filteredMembers = bannedMembers.filter(
    (member) =>
      !searchQuery ||
      member.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.userOid?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleUnban = async () => {
    await unbanMutation.mutateAsync(unbanDialog.member.userOid);
    setUnbanDialog({ open: false, member: null });
  };

  // Only owners, super admins, and admins can manage banned members
  const canManage =
    userRole === 'owner' || userRole === 'superAdmin' || userRole === 'admin';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Banned Members</h1>
        <p className="text-zinc-400">View and manage banned members</p>
      </div>

      {/* Search */}
      <Card className="bg-zinc-900/50 border-white/10 backdrop-blur-sm">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              placeholder="Search banned members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-zinc-900/50 border-white/10 text-white placeholder:text-zinc-500 h-10 rounded-xl focus:ring-violet-500/50"
            />
          </div>
        </CardContent>
      </Card>

      {/* Banned Members List */}
      <Card className="bg-zinc-900/50 border-white/10 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Ban className="w-5 h-5 text-red-400" />
            {filteredMembers.length} Banned Member
            {filteredMembers.length !== 1 ? 's' : ''}
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Members who are banned from this community
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredMembers.length === 0 ? (
            <div className="text-center py-12">
              <UserCheck className="w-12 h-12 mx-auto mb-4 text-green-400 opacity-50" />
              <p className="text-zinc-400">No banned members</p>
              <p className="text-sm text-zinc-500 mt-1">
                {searchQuery
                  ? 'No results match your search'
                  : 'All members are in good standing'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMembers.map((member) => (
                <div
                  key={member.userOid}
                  className="flex items-center justify-between p-4 rounded-xl bg-red-500/5 border border-red-500/10 hover:bg-red-500/10 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {/* Avatar */}
                    <div className="relative">
                      {member.avatar ? (
                        <img
                          src={member.avatar}
                          alt={member.username}
                          className="w-10 h-10 rounded-full object-cover grayscale"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                          <span className="text-zinc-500 font-medium">
                            {member.username?.[0]?.toUpperCase() || '?'}
                          </span>
                        </div>
                      )}
                      <div className="absolute -bottom-1 -right-1 p-0.5 bg-red-600 rounded-full">
                        <Ban className="w-3 h-3 text-white" />
                      </div>
                    </div>

                    {/* Info */}
                    <div>
                      <p className="font-medium text-white">
                        {member.username || 'Unknown'}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Banned{' '}
                          {member.bannedAt
                            ? new Date(member.bannedAt).toLocaleDateString()
                            : 'Unknown date'}
                        </span>
                      </div>
                      {member.reason && (
                        <p className="text-xs text-red-400/80 mt-1">
                          Reason: {member.reason}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  {canManage && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUnbanDialog({ open: true, member })}
                      className="border-green-900/50 text-green-400 hover:bg-green-950/30 hover:text-green-300"
                    >
                      <UserCheck className="w-4 h-4 mr-2" />
                      Unban
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unban Dialog */}
      <Dialog
        open={unbanDialog.open}
        onOpenChange={(open) =>
          !open && setUnbanDialog({ open: false, member: null })
        }
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-green-400" />
              Unban Member
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Are you sure you want to unban{' '}
              <span className="text-white font-medium">
                {unbanDialog.member?.username}
              </span>
              ? They will be able to rejoin the community.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUnbanDialog({ open: false, member: null })}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUnban}
              disabled={unbanMutation.isPending}
              className="bg-green-600 hover:bg-green-700"
            >
              {unbanMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Unban Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
